import {
  BookingConfigurationResponse,
  CreateSubmissionRequest,
  CreateSubmissionResponse,
  FindSubmissionsResponse,
  InstallmentResponse,
  LeadActivity,
  LeadPrivacyRequestBody,
  ProspectData,
  SavedLeadInfo,
  SavedSubmissionRecord,
  SubmissionByReserveIdResponse,
  SubmissionByProspectResponse,
  UnsubscribeRequestBody,
  ApplicantData,
} from '@/types/salesforce';
import {
  isSalesforceId,
  soqlStartsWith,
  soqlString,
} from '../recordAccess/salesforceId';
import { salesforceApi } from '../axiosConfig';
import { isAxiosError } from 'axios';

function assertSalesforceId(id: string): void {
  if (!isSalesforceId(id)) {
    throw new Error(`Not a Salesforce ID: ${String(id).slice(0, 40)}`);
  }
}

function query<T>(soql: string) {
  return salesforceApi.get<T>('data/v60.0/query', { params: { q: soql } });
}

export class InternalSalesforceService {
  static async createSubmission(data: CreateSubmissionRequest) {
    const response = await salesforceApi.post<CreateSubmissionResponse>(
      'data/v60.0/sobjects/Submission__c',
      data
    );
    return response;
  }

  static async updateSubmission(
    id: string,
    data: Partial<CreateSubmissionRequest>
  ) {
    assertSalesforceId(id);
    const response = await salesforceApi.patch(
      `data/v60.0/sobjects/Submission__c/Id/${id}`,
      data
    );
    return response;
  }

  static async partialUpdateSubmission(
    id: string,
    data: Partial<CreateSubmissionRequest>
  ) {
    assertSalesforceId(id);
    const response = await salesforceApi.patch(
      `data/v60.0/sobjects/Submission__c/Id/${id}`,
      data
    );
    return response;
  }

  static async getSavedSubmission(id: string) {
    assertSalesforceId(id);
    const response = await salesforceApi.get<SavedSubmissionRecord>(
      `data/v60.0/sobjects/Submission__c/${id}`
    );
    return response;
  }

  static async getSubmissionLinks(id: string) {
    assertSalesforceId(id);
    const response = await salesforceApi.get<{
      Prospect__c: string | null;
      Lead__c: string | null;
      RML_Submission__c: string | null;
    }>(`data/v60.0/sobjects/Submission__c/${id}`, {
      params: { fields: 'Prospect__c,Lead__c,RML_Submission__c' },
    });
    return response.data;
  }

  static async getSubmissionLeadId(submissionId: string) {
    const { Lead__c } =
      await InternalSalesforceService.getSubmissionLinks(submissionId);
    if (!Lead__c) throw new Error(`Submission ${submissionId} has no lead`);
    return Lead__c;
  }

  static async getCreatedDate(
    type: 'Submission__c' | 'Prospect__c' | 'Applicant__c',
    id: string
  ): Promise<number | null> {
    assertSalesforceId(id);
    try {
      const { data } = await salesforceApi.get<{ CreatedDate: string }>(
        `data/v60.0/sobjects/${type}/${id}`,
        { params: { fields: 'CreatedDate' } }
      );
      return Date.parse(data.CreatedDate);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) return null;
      throw error;
    }
  }

  static async unsubscribe(data: UnsubscribeRequestBody) {
    const response = await salesforceApi.patch<string>(
      'apexrest/setLeadOptOut',
      data
    );
    return response;
  }

  static async logSubmissionCreating(data: {
    Submission__c: string;
    RawJSON__c: string;
  }) {
    const response = await salesforceApi.post<{
      id: string;
      success: boolean;
      errors: [];
    }>('data/v60.0/sobjects/SubmissionLog__c', data);
    return response;
  }

  static async updateLogSubmission(
    data: {
      Submission__c: string;
      RawJSON__c: string;
    },
    id: string
  ) {
    assertSalesforceId(id);
    const response = await salesforceApi.patch(
      `data/v60.0/sobjects/SubmissionLog__c/Id/${id}`,
      data
    );
    return response;
  }

  static async updateLeadPrivacy(data: LeadPrivacyRequestBody) {
    const response = await salesforceApi.post<{
      message: string;
      statusCode: string;
      leadId: string;
    }>('apexrest/setLeadOptOut', data);
    return response;
  }

  static async getLeadInfo(leadId: string) {
    assertSalesforceId(leadId);
    const response = await salesforceApi.get<SavedLeadInfo>(
      `data/v60.0/sobjects/Submission__c/${leadId}/Lead__r`
    );
    return response;
  }

  static async updateLeadInfo(leadId: string, data: Partial<SavedLeadInfo>) {
    assertSalesforceId(leadId);
    const response = await salesforceApi.patch(
      `data/v60.0/sobjects/Submission__c/${leadId}/Lead__r`,
      data
    );
    return response;
  }

  static async searchAgentName(email: string) {
    const response = await query<{
      totalSize: number;
      done: boolean;
      records: Array<{
        attributes: Array<{ type: string; url: string }>;
        Id: string;
        FirstName: string;
        LastName: string;
      }>;
    }>(
      `SELECT Id, FirstName, LastName FROM User WHERE Email = ${soqlString(email)}`
    );
    return response;
  }

  static async assignReferralAgent(submissionId: string, mail: string) {
    const leadId =
      await InternalSalesforceService.getSubmissionLeadId(submissionId);
    const response = await salesforceApi.patch(
      `data/v60.0/sobjects/Lead/${leadId}`,
      { Regal_Assigned_Agent__c: mail }
    );
    return response;
  }

  static async getProspectRecord(id: string) {
    assertSalesforceId(id);
    const response = await salesforceApi.get<ProspectData>(
      `data/v60.0/sobjects/Prospect__c/${id}`
    );
    return response;
  }

  static async updateProspect(id: string, data: Partial<ProspectData>) {
    assertSalesforceId(id);
    const response = await salesforceApi.patch(
      `data/v60.0/sobjects/Prospect__c/${id}`,
      data
    );
    return response;
  }

  static async getApplicantRecord(id: string) {
    assertSalesforceId(id);
    const response = await salesforceApi.get<ApplicantData>(
      `data/v66.0/sobjects/Applicant__c/${id}`
    );
    return response;
  }

  static async updateApplicant(id: string, data: Partial<ApplicantData>) {
    assertSalesforceId(id);
    const response = await salesforceApi.patch(
      `data/v60.0/sobjects/Applicant__c/${id}`,
      data
    );
    return response;
  }

  static async prospectCreditPullUpdate(id: string) {
    assertSalesforceId(id);
    const response = await salesforceApi.patch(
      `data/v60.0/sobjects/Submission__c/Id/${id}`,
      { EQXCreditPullStatus__c: 'Success' }
    );
    return response;
  }

  static async createActivity({
    id,
    activity,
    duration,
  }: {
    id: string;
    activity: string;
    duration: number;
  }) {
    const response = await salesforceApi.post(
      'data/v60.0/sobjects/ClientActivityTracker__c',
      {
        ActivityTimestamp__c: new Date().toISOString(),
        Activity__c: activity,
        SessionLength__c: duration,
        Lead__c: id,
      }
    );
    return response;
  }

  static async getActivities({ id }: { id: string }) {
    assertSalesforceId(id);
    const response = await query<LeadActivity>(
      `SELECT ActivityTimestamp__c, Activity__c, Lead__c, SessionLength__c FROM ClientActivityTracker__c WHERE Lead__c = ${soqlString(id)}`
    );
    return response;
  }

  static async getInstallment(id: string): Promise<InstallmentResponse> {
    try {
      assertSalesforceId(id);
      const response = await query<InstallmentResponse>(
        `SELECT Id, Creditor__c, OriginalBalance__c, MonthlyPayment__c, OriginalTerm__c, Submission__c, CurrentBalance__c FROM Installment__c WHERE Submission__c = ${soqlString(id)}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async getSubmissionByReserveId(id: string) {
    const response = await query<SubmissionByReserveIdResponse>(
      `SELECT FirstNameSO__c, LastNameSO__c, Id, RecordType.DeveloperName, RML_Submission__c, CreatedDate, LastModifiedDate FROM Submission__c WHERE RML_Submission__c = ${soqlString(id)} ORDER BY LastModifiedDate DESC`
    );
    return response;
  }

  static async getBookingConfig() {
    const response = await salesforceApi.get<BookingConfigurationResponse>(
      'data/v60.0/sobjects/WebsiteConfiguration__c/a0DUl00000QNSvfMAH'
    );
    return response;
  }

  static async updateLeadCallStatus(submissionId: string) {
    const leadId =
      await InternalSalesforceService.getSubmissionLeadId(submissionId);
    const response = await salesforceApi.patch(
      `data/v60.0/sobjects/Lead/Id/${leadId}`,
      { CallMeNow__c: true, Call_Me_Now_Pending__c: true }
    );
    return response;
  }

  static async findSubmissionId(email: string, zipCode: string) {
    const response = await query<FindSubmissionsResponse>(
      `SELECT FirstName, LastName, Id, ActiveCalBooking__c, Active_Submission__c, Submission_Object_ID__c, LastModifiedDate FROM Lead WHERE Email = ${soqlString(email)} AND Old_Lead__c = false AND RecordTypeId = '012Ul0000021NHZIA2' AND PostalCode LIKE ${soqlStartsWith(zipCode)} ORDER BY LastModifiedDate DESC`
    );
    return response;
  }

  static async getSubmissionByProspect(id: string) {
    assertSalesforceId(id);
    const response = await query<SubmissionByProspectResponse>(
      `SELECT FirstNameSO__c, LastNameSO__c, Id, Prospect__c, CreatedDate, LastModifiedDate FROM Submission__c WHERE Prospect__c = ${soqlString(id)} ORDER BY LastModifiedDate DESC`
    );
    return response;
  }
}
